import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight,
  Link2,
  Database,
  Server,
  Cloud,
  Layers,
  CheckCircle2,
  Zap
} from 'lucide-react';

const integrations = [
  {
    name: 'SAP',
    logo: '🔷',
    category: 'ERP',
    description: 'Seamless integration with SAP ERP for real-time data synchronization.',
    features: ['Material master sync', 'Purchase order integration', 'Vendor management', 'Financial posting'],
  },
  {
    name: 'Oracle',
    logo: '🔶',
    category: 'ERP',
    description: 'Connect with Oracle ERP Cloud for unified business processes.',
    features: ['Customer sync', 'Order management', 'Inventory integration', 'Reporting'],
  },
  {
    name: 'Microsoft Dynamics',
    logo: '🟦',
    category: 'ERP',
    description: 'Integration with Dynamics 365 for end-to-end business operations.',
    features: ['CRM integration', 'Sales automation', 'Customer data sync', 'Workflow triggers'],
  },
  {
    name: 'Tally',
    logo: '🟩',
    category: 'Accounting',
    description: 'Connect with Tally for financial data and invoice management.',
    features: ['Invoice sync', 'Ledger integration', 'GST compliance', 'Payment tracking'],
  },
  {
    name: 'Outlook / Gmail',
    logo: '📧',
    category: 'Communication',
    description: 'Email integration for RFQ capture and automated notifications.',
    features: ['RFQ email capture', 'Auto-notifications', 'Thread tracking', 'Attachment handling'],
  },
  {
    name: 'Custom APIs',
    logo: '⚙️',
    category: 'Custom',
    description: 'RESTful API for custom integrations with any system.',
    features: ['REST API', 'Webhooks', 'OAuth 2.0', 'Rate limiting'],
  },
];

const apiFeatures = [
  {
    icon: Database,
    title: 'Data Sync',
    description: 'Real-time bidirectional data synchronization with your existing systems.',
  },
  {
    icon: Server,
    title: 'REST API',
    description: 'Comprehensive REST API with full documentation and SDKs.',
  },
  {
    icon: Cloud,
    title: 'Webhooks',
    description: 'Event-driven webhooks for real-time notifications and triggers.',
  },
  {
    icon: Layers,
    title: 'Pre-built Connectors',
    description: 'Ready-to-use connectors for popular ERP and business systems.',
  },
];

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const IntegrationsPage = () => {
  return (
    <div className="pt-24" data-testid="integrations-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium">
              <Link2 className="w-4 h-4" />
              Integrations
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Connect Your Ecosystem
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Cost Genie integrates seamlessly with your existing ERP, accounting, and 
              business systems. No manual data re-entry, no disconnected silos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Integration Grid */}
      <section className="py-24" data-testid="integrations-grid">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Supported Systems</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              Pre-built Integrations
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration, index) => (
              <AnimatedSection key={integration.name}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="glass-card p-6 h-full"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-2xl">
                      {integration.logo}
                    </div>
                    <div>
                      <h3 className="font-semibold">{integration.name}</h3>
                      <span className="text-white/50 text-xs">{integration.category}</span>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm mb-4">{integration.description}</p>
                  <ul className="space-y-2">
                    {integration.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/50">
                        <CheckCircle2 className="w-4 h-4 text-[#00D4FF]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* API Features */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Developer Tools</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              API-First Architecture
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Build custom integrations with our comprehensive API and developer tools.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {apiFeatures.map((feature, index) => (
              <AnimatedSection key={feature.title}>
                <div className="glass-card p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#00D4FF]/10 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-[#00D4FF]" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Process */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Implementation</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              Simple Integration Process
            </h2>
          </AnimatedSection>

          <AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Assessment', description: 'We analyze your current systems and integration requirements.' },
                { step: '2', title: 'Configuration', description: 'Configure connectors and map data fields to your specifications.' },
                { step: '3', title: 'Go Live', description: 'Test, validate, and go live with ongoing support.' },
              ].map((item, index) => (
                <div key={item.step} className="glass-card p-8 text-center relative">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#00D4FF] text-black font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2 mt-4">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Need a Custom Integration?
                </h2>
                <p className="text-white/60 mb-8 max-w-xl mx-auto">
                  Our team can build custom connectors for any system. Let's discuss your requirements.
                </p>
                <Link
                  to="/demo"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all"
                  data-testid="integrations-demo-btn"
                >
                  Talk to Our Team
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default IntegrationsPage;
